import {
  Control,
  Controller,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { RulesEngineFormValues } from '../rules-engine-form-page-old';
import { SectionHeader } from '../../../../shared/section-header';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface GroupFiltersStepProps {
  control: Control<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  clearErrors: UseFormClearErrors<RulesEngineFormValues>;
}

const memberCountOperatorOptions: {
  value: NonNullable<
    RulesEngineFormValues['groupFilter']
  >['memberCountOperator'];
  label: string;
}[] = [
  { value: 'equals', label: 'equals' },
  { value: 'greaterThan', label: 'greater than' },
  { value: 'lessThan', label: 'less than' },
  { value: 'greaterThanOrEqual', label: 'greater than or equal to' },
  { value: 'lessThanOrEqual', label: 'less than or equal to' },
];

export default function RulesEngineGroupFiltersStep({
  control,
  watch,
  setValue,
  clearErrors,
}: GroupFiltersStepProps) {
  const hasGroupNameFilter = watch('groupFilter.hasGroupNameFilter');
  const hasMemberCountFilter = watch('groupFilter.hasMemberCountFilter');
  const groupNameMode = watch('groupFilter.groupNameMode') ?? 'name';
  const storedMemberCountMode =
    watch('groupFilter.memberCountMode') ?? 'single';
  const triggerEvent = watch('triggerInfo.triggerEvent');

  // The "range" member-count option is only valid for the "Any Membership changes" event.
  const isAnyMembershipChange = triggerEvent === 'UPDATE_GROUP_MEMBER';

  const memberCountMode =
    storedMemberCountMode === 'range' && !isAnyMembershipChange
      ? 'single'
      : storedMemberCountMode;

  return (
    <Stack gap={2} width={'100%'}>
      <SectionHeader>
        Group Filters <span style={{ fontWeight: 400 }}>(optional)</span>
      </SectionHeader>
      <Typography variant="caption">
        You can optionally provide a filter on group information. This will act
        as an "and" condition with other filters.
      </Typography>

      {/* Filter By Group Name */}
      <Controller
        name="groupFilter.hasGroupNameFilter"
        control={control}
        render={({ field: props }) => (
          <FormControlLabel
            sx={{ width: 'fit-content' }}
            control={
              <Checkbox
                {...props}
                checked={!!props.value}
                onChange={(e) => {
                  props.onChange(e.target.checked);
                  if (!e.target.checked) {
                    clearErrors([
                      'groupFilter.groupName',
                      'groupFilter.groupNameRegex',
                    ]);
                  }
                }}
              />
            }
            label="Filter By Group Name"
          />
        )}
      />

      <Box sx={{ pl: '16px' }}>
        <Controller
          name="groupFilter.groupNameMode"
          control={control}
          render={({ field: modeField }) => (
            <RadioGroup
              value={modeField.value ?? 'name'}
              onChange={(e) => modeField.onChange(e.target.value)}
            >
              <Stack direction="row" alignItems="flex-start" gap={1}>
                <Radio value="name" disabled={!hasGroupNameFilter} />
                <Controller
                  name="groupFilter.groupName"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Group Name"
                      required
                      size="small"
                      sx={{ flexGrow: 1, maxWidth: 360 }}
                      disabled={!hasGroupNameFilter || groupNameMode !== 'name'}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || ' '}
                    />
                  )}
                />
                <Controller
                  name="groupFilter.groupNameExact"
                  control={control}
                  render={({ field: exactField }) => (
                    <FormControlLabel
                      sx={{ mt: '2px' }}
                      control={
                        <Checkbox
                          {...exactField}
                          checked={!!exactField.value}
                          onChange={(e) =>
                            exactField.onChange(e.target.checked)
                          }
                          disabled={
                            !hasGroupNameFilter || groupNameMode !== 'name'
                          }
                        />
                      }
                      label="Exact"
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" alignItems="flex-start" gap={1} mt={1}>
                <Radio value="regex" disabled={!hasGroupNameFilter} />
                <Controller
                  name="groupFilter.groupNameRegex"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ''}
                      label="Regular Expression"
                      required
                      size="small"
                      sx={{ flexGrow: 1, maxWidth: 360 }}
                      disabled={
                        !hasGroupNameFilter || groupNameMode !== 'regex'
                      }
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || ' '}
                    />
                  )}
                />
              </Stack>
            </RadioGroup>
          )}
        />
      </Box>

      {/* Filter By Member Count */}
      <Controller
        name="groupFilter.hasMemberCountFilter"
        control={control}
        render={({ field: props }) => (
          <FormControlLabel
            sx={{ width: 'fit-content' }}
            control={
              <Checkbox
                {...props}
                checked={!!props.value}
                onChange={(e) => {
                  props.onChange(e.target.checked);
                  if (!e.target.checked) {
                    clearErrors([
                      'groupFilter.memberCount',
                      'groupFilter.memberCountRangeLessThan',
                      'groupFilter.memberCountRangeGreaterThan',
                    ]);
                  } else {
                    if (!watch('groupFilter.memberCountOperator')) {
                      setValue('groupFilter.memberCountOperator', 'equals');
                    }
                    if (!watch('groupFilter.memberCountMode')) {
                      setValue('groupFilter.memberCountMode', 'single');
                    }
                  }
                }}
              />
            }
            label="Filter By Member Count"
          />
        )}
      />

      <Box sx={{ pl: '16px' }}>
        <Controller
          name="groupFilter.memberCountMode"
          control={control}
          render={({ field: modeField }) => (
            <RadioGroup
              value={memberCountMode}
              onChange={(e) => modeField.onChange(e.target.value)}
            >
              {/* Single operator + value */}
              <Stack direction="row" alignItems="flex-start" gap={1.5}>
                <Radio value="single" disabled={!hasMemberCountFilter} />
                <Typography sx={{ mt: '8px', minWidth: 150 }}>
                  Group Member Count
                </Typography>
                <Controller
                  name="groupFilter.memberCountOperator"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Operator"
                      size="small"
                      sx={{ flexGrow: 1, maxWidth: 250 }}
                      value={field.value ?? 'equals'}
                      disabled={
                        !hasMemberCountFilter || memberCountMode !== 'single'
                      }
                    >
                      {memberCountOperatorOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  name="groupFilter.memberCount"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      value={field.value ?? 0}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      type="number"
                      size="small"
                      sx={{ width: 110 }}
                      inputProps={{ min: 0 }}
                      disabled={
                        !hasMemberCountFilter || memberCountMode !== 'single'
                      }
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Stack>

              {isAnyMembershipChange && (
                <Stack direction="row" alignItems="flex-start" gap={1.5} mt={3}>
                  <Radio value="range" disabled={!hasMemberCountFilter} />
                  <Stack gap={1.5}>
                    <Typography sx={{ mt: '8px' }}>
                      Group Member Count
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Typography>is less than</Typography>
                      <Controller
                        name="groupFilter.memberCountRangeLessThan"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value ?? 0}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            type="number"
                            size="small"
                            sx={{ width: 90 }}
                            inputProps={{ min: 0 }}
                            disabled={
                              !hasMemberCountFilter ||
                              memberCountMode !== 'range'
                            }
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                      <Typography>or greater than</Typography>
                      <Controller
                        name="groupFilter.memberCountRangeGreaterThan"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value ?? 0}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            type="number"
                            size="small"
                            sx={{ width: 90 }}
                            inputProps={{ min: 0 }}
                            disabled={
                              !hasMemberCountFilter ||
                              memberCountMode !== 'range'
                            }
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </RadioGroup>
          )}
        />
      </Box>
    </Stack>
  );
}
